import { Box, Divider, IconButton, Typography } from '@mui/material';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Collapse from 'components/collapse';
import { GenerativeLabelsType } from 'entities/Files/types';
import { ColorPalette } from 'shared/consts/colorPalette';
import { Icon } from 'shared/ui/Icon';
import { useFileEditorStore } from 'store/file-editor';
import GenerativeLabelsModal from '../generative-labels-modal';

type LabelItemProps = {
	label: GenerativeLabelsType;
	isLast: boolean;
	onEdit: () => void;
	onDelete: () => void;
};

const LabelItem = ({ label, isLast, onEdit, onDelete }: LabelItemProps) => (
	<Box sx={{ py: 0.25 }}>
		<Box
			sx={{
				display: 'flex',
				alignItems: 'flex-start',
				justifyContent: 'space-between',
				gap: 1,
				borderRadius: '10px',
				p: 1.25,
			}}
		>
			<Box sx={{ minWidth: 0, flex: 1 }}>
				<Typography variant={'body2'} color={ColorPalette.astronaut}>
					{label.name}
				</Typography>
				<Typography variant={'body2'} color={ColorPalette.baliHai}>
					{label.description}
				</Typography>
			</Box>
			<Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
				<IconButton onClick={onEdit} size={'small'} sx={{ padding: '2px' }}>
					<Icon
						id={'edit'}
						color={ColorPalette.royalBlue}
						style={{ width: '18px', height: '18px' }}
					/>
				</IconButton>
				<IconButton onClick={onDelete} size={'small'} sx={{ padding: '2px' }}>
					<Icon
						id={'close_round'}
						color={ColorPalette.violetRed}
						style={{ width: '18px', height: '18px' }}
					/>
				</IconButton>
			</Box>
		</Box>
		{!isLast && <Divider sx={{ marginTop: 0.5 }} />}
	</Box>
);

const GenerativeResearch: FC = () => {
	const { t } = useTranslation();
	const { fileDetails, setFileDetailsV2 } = useFileEditorStore();
	const [isOpenModal, setIsOpenModal] = useState(false);
	const [labelsDetails, setLabelsDetails] = useState<GenerativeLabelsType & { labelIndex: number }>();

	const closeModal = () => {
		setIsOpenModal(false);
		setLabelsDetails(undefined);
	};

	const addResearch = () => {
		setIsOpenModal(true);
		setLabelsDetails(undefined);
	};

	return (
		<>
			<GenerativeLabelsModal
				open={isOpenModal}
				handleClose={closeModal}
				labelsDetails={labelsDetails}
			/>

			<Collapse
				label={`${t('files.labels.research')} ${fileDetails?.labels.length}`}
				headerExtra={
					<IconButton onClick={addResearch} size={'small'} sx={{ padding: '2px' }}>
						<Icon
							id={'add_round'}
							color={ColorPalette.royalBlue}
							style={{ width: '20px', height: '20px' }}
						/>
					</IconButton>
				}
			>
				{fileDetails?.labels.map((item, i) => (
					<LabelItem
						key={`${item.name}-${i}`}
						label={item}
						isLast={i === fileDetails.labels.length - 1}
						onEdit={() => {
							setLabelsDetails({ ...item, labelIndex: i });
							setIsOpenModal(true);
						}}
						onDelete={() => {
							setFileDetailsV2({
								...fileDetails,
								labels: fileDetails.labels.filter((_, labelIndex) => labelIndex !== i),
							});
						}}
					/>
				))}
			</Collapse>
		</>
	);
};

export default GenerativeResearch;
